import { Box, CircularProgress, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import type { ResolvedAction } from '../../../runtime/forms/action-resolution';
import type { ActionDescriptor, SubmissionState } from '@finance/contracts';
import type { ActionExecutionContext } from '../../../runtime/actions/action-executor';
import { ActionRenderer } from '../ActionRenderer.tsx';
import { useAppI18n } from '../../../i18n/use-app-i18n';
import { formTokens } from './form.tokens.ts';

export interface FormActionBarProps {
  actions: ResolvedAction[];
  onAction: (
    action: ActionDescriptor,
    context?: ActionExecutionContext,
  ) => void | Promise<void>;
  pending?: boolean;
  isDirty?: boolean;
  submissionState?: SubmissionState;
}

export function FormActionBar({
  actions,
  onAction,
  pending = false,
  isDirty = false,
  submissionState = 'idle',
}: FormActionBarProps) {
  const { text } = useAppI18n();
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (submissionState !== 'succeeded' || isDirty || pending) {
      setShowSuccess(false);
      return;
    }
    setShowSuccess(true);
    const timer = window.setTimeout(() => setShowSuccess(false), 4000);
    return () => window.clearTimeout(timer);
  }, [submissionState, isDirty, pending]);

  const hasStatus =
    pending || isDirty || showSuccess || submissionState === 'failed';
  if (actions.length === 0 && !hasStatus) return null;

  const groupRank: Record<string, number> = {
    primary: 0,
    secondary: 1,
    tertiary: 2,
    danger: 3,
    navigation: 4,
  };
  const sorted = [...actions].sort((left, right) => {
    const alignDifference =
      (left.alignment === 'start' ? 0 : 1) - (right.alignment === 'start' ? 0 : 1);
    if (alignDifference !== 0) return alignDifference;
    const groupDifference =
      (groupRank[left.group] ?? 99) - (groupRank[right.group] ?? 99);
    if (groupDifference !== 0) return groupDifference;
    return left.order - right.order;
  });

  const startActions = sorted.filter(action => action.alignment === 'start');
  const endActions = sorted.filter(action => action.alignment !== 'start');

  const renderAction = (resolved: ResolvedAction) => (
    <ActionRenderer
      key={resolved.action.id}
      action={{ ...resolved, disabled: resolved.disabled || pending }}
      onAction={(next, context) => onAction(next.action, context)}
      context={{ source: 'form' }}
    />
  );

  const actionGroupSx = {
    display: 'flex',
    gap: formTokens.spacing.actionBarGap,
    alignItems: 'stretch',
    flexWrap: 'wrap',
    width: { xs: '100%', sm: 'auto' },
    minWidth: 0,
    '& > *, & .MuiButton-root': {
      flex: { xs: '1 1 100%', sm: '0 0 auto' },
      maxWidth: '100%',
    },
  } as const;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'auto minmax(0, 1fr) auto' },
        alignItems: 'center',
        gap: formTokens.spacing.actionBarGap,
        position: formTokens.actionBar.position,
        bottom: formTokens.actionBar.bottom,
        bgcolor: formTokens.colors.actionBarBackground,
        py: formTokens.spacing.actionBarPaddingY,
        px: { xs: 0.5, sm: 1 },
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        boxShadow: '0 -4px 12px -4px rgba(0, 0, 0, 0.08)',
        zIndex: formTokens.actionBar.zIndex,
        minWidth: 0,
      }}
    >
      {startActions.length > 0 && (
        <Box sx={{ ...actionGroupSx, order: { xs: 3, sm: 1 } }}>
          {startActions.map(renderAction)}
        </Box>
      )}

      <Box
        aria-live="polite"
        sx={{
          minWidth: 0,
          minHeight: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          order: { xs: 1, sm: 2 },
        }}
      >
        {pending && <CircularProgress size={18} aria-label={text('common.loading')} />}
        {isDirty && !pending && (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {text('form.unsavedChanges')}
          </Typography>
        )}
        {submissionState === 'failed' && !pending && (
          <Typography variant="caption" color="error.main" sx={{ overflowWrap: 'anywhere' }}>
            {text('form.saveFailed')}
          </Typography>
        )}
        {showSuccess && (
          <Typography variant="caption" color="success.main" sx={{ overflowWrap: 'anywhere' }}>
            {text('form.saved')}
          </Typography>
        )}
      </Box>

      {endActions.length > 0 && (
        <Box
          sx={{
            ...actionGroupSx,
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            order: { xs: 2, sm: 3 },
          }}
        >
          {endActions.map(renderAction)}
        </Box>
      )}
    </Box>
  );
}
